import * as React from "react";
import { useCallback, useState } from "react";
import styled from "@mui/material/styles/styled";
import { QualifiedFeature } from "fe-common/graphql";
import { DiagnosisView } from "../DiagnosisView";
import { useDiseasesSections } from "../hooks/useDiseasesSections";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../../../util/aKaunGeneralUtil";
import { useDiagnosticAgentDDRequired } from "../../../context/diagnosticAgent";
import { Slide, Theme } from "@mui/material";
import { DDSortType } from "fe-common/hooks/sortFeaturesWithCrs";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { MobileOnly } from "../util/mobileOnly";
import { DesktopOnly } from "../util/desktopOnly";
import { ImproveDiagnosisButton } from "../nextStep/improveDiagnosisButton";
import { DataInputMobileSidePadding } from "../common/styles/consts";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";
import { SantanaPlan } from "../santana/santanaPlan";
import { ImproveDiagnosisPopup } from "../improveDiagnosis/improveDiagnosisPopup";
import { DebugSheet } from "../DebugSheet";
import { useScreenSize } from "../util/useScreenSize";
import { usePatientCase } from "../usePatientCase";
import {
  ColoredRoundActionButton,
  ColoredRoundActionButtonProps,
} from "fe-common/ui/coloredRoundActionButton";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ddListZIndex } from "fe-common/ui/stacking";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { DDStatus } from "../updateDDToState";

type SantanaAndThemeProps = { theme?: Theme };

//region [[ Styles ]]
const DdPanelView = styled("div")(
  ({ theme, disabled }: { theme?: Theme; disabled: boolean }) => ({
    pointerEvents: disabled ? "none" : "unset",
    opacity: disabled ? 0.3 : 1,
    [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
      marginBottom: "-65px", // Kill the bottom 65px padding set in contentRouter since scroll is inherent
    },
    [theme.breakpoints.down(MobileView)]: {
      margin: "-7px", // Kill the contentWrapper 7px padding
      backgroundColor: "white",
    },
  }),
);

type RecommendedPlanContainerProps = {
  theme?: Theme;
  "data-showdebug"?: "true" | "false";
};

const RecommendedPlanContainer = styled("div")(
  ({ theme, "data-showdebug": showdebug }: RecommendedPlanContainerProps) => ({
    position: "absolute",
    marginLeft: "20px",
    display: "flex",
    justifyContent: "center",
    right: "20px",
    width: "41%",
    height: `calc(100% - ${showdebug === "true" ? 36 : 0}px)`,
  }),
);

const DDListContainer = styled("div")(({ theme }: { theme?: Theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    paddingRight: `${DataInputMobileSidePadding}px`,
    paddingLeft: `${DataInputMobileSidePadding}px`,
  },
}));

const DDList = styled(DiagnosisView)(({ theme }: SantanaAndThemeProps) => ({
  height: "100%",
  width: "100%",
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    width: "58%",
    left: 0,
    position: "absolute",
    padding: "0 52px",
  },
}));

const DDSpaceKeeper = styled("div")(({ theme }) => ({
  height: `${bottomActionAreaHeight + 20}px`,
  [theme.breakpoints.down(MobileView)]: {
    height: "77px",
  },
}));
const bottomActionAreaHeight = 72;
const bottomActionAreaWidth = 289;
const BottomActionArea = styled("div")({
  width: `${bottomActionAreaWidth}px`,
  display: "flex",
  justifyContent: "center",
  marginTop: "20px",
  height: `${bottomActionAreaHeight}px`,
  position: "absolute",
  left: `calc(30% - ${bottomActionAreaWidth / 2}px)`,
  bottom: 0,
  zIndex: ddListZIndex,
});

const BottomActionButton = styled(
  ({ ...props }: ColoredRoundActionButtonProps) => (
    <ColoredRoundActionButton kahunColor={KahunColorMap.Primary} {...props} />
  ),
)({
  color: KahunColorMap.White,
  width: "100%",
  hegiht: "100%",
  maxWidth: "auto",
  borderRadius: "56px",
  fontWeight: 500,
  fontSize: "20px",
  padding: "26px 55px",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface DdPanelProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[ Texts ]]
const UPDATE_DIAGNOSIS = "Update";
//endregion [[ Texts ]]

export const DdPanel = ({ ...props }: DdPanelProps) => {
  const [sortType, setSortType] = useState<DDSortType>(
    DDSortType.BySuspicionIndex,
  );

  const {
    editPanelPopupProps,
    improveDiagnosisPopupOpen,
  } = useDiagnosticNavigation();

  const handleSortChange = (sortType: DDSortType) => {
    setSortType(sortType);
    evt({
      category: CATEGORY.AGENT_DD_LIST,
      action: ACTION.SORT_TYPE,
      label: sortType,
    });
  };

  const [diseaseSelection, setDiseaseSelection] = React.useState<
    QualifiedFeature
  >(null);

  const {
    featuresSections,
    ddData,
    loading: ddLoading,
    ddStatus,
  } = useDiseasesSections(sortType);

  const [isDDRequired] = useDiagnosticAgentDDRequired();
  const { isMobile } = useScreenSize();

  const { isPendingChange, applyPendingFindings } = usePatientCase();

  const { setImproveDiagnosisPopupOpen } = useDiagnosticNavigation();

  const showDebug = getFeatureFlags("debugSheet");

  const updateDiagnosisClicked = useCallback(() => {
    evt({
      category: CATEGORY.PLAN,
      action: ACTION.CALCULATE,
    });
    applyPendingFindings();
    setImproveDiagnosisPopupOpen(false);
  }, [setImproveDiagnosisPopupOpen, applyPendingFindings]);

  // disable diagnose button for now
  const showDiagnoseButton =
    isPendingChange() &&
    false &&
    (!editPanelPopupProps || !editPanelPopupProps.open) &&
    !improveDiagnosisPopupOpen;

  return (
    <DdPanelView disabled={!isDDRequired}>
      <DesktopOnly>
        <RecommendedPlanContainer data-showdebug={showDebug ? "true" : "false"}>
          <SantanaPlan />
        </RecommendedPlanContainer>
        <ImproveDiagnosisPopup />
      </DesktopOnly>
      <DDListContainer>
        <DDList
          ddStatus={ddStatus}
          loading={ddLoading}
          data={ddData}
          selection={diseaseSelection}
          setSelection={setDiseaseSelection}
          featuresSections={featuresSections}
          sortType={sortType}
          handleSortChange={handleSortChange}
        />
        {/* Keep a gap for the teaser - */}
        <Slide
          in={
            (!isMobile && showDiagnoseButton) ||
            (isMobile && ddData && !ddLoading)
          }
          direction="up"
          mountOnEnter
          unmountOnExit
        >
          <DDSpaceKeeper />
        </Slide>
      </DDListContainer>
      {ddStatus && ddStatus.status === DDStatus.SUCCESS && !ddLoading && (
        <MobileOnly>
          <ImproveDiagnosisButton />
        </MobileOnly>
      )}
      <DesktopOnly>
        {showDebug && ddData?.debugSheets && (
          <DebugSheet debugSheets={ddData.debugSheets} />
        )}
        <BottomActionArea>
          <Slide
            direction={"up"}
            in={showDiagnoseButton}
            mountOnEnter
            unmountOnExit
          >
            <div>
              <BottomActionButton
                onClick={updateDiagnosisClicked}
                disabled={!showDiagnoseButton}
                label={UPDATE_DIAGNOSIS}
              />
            </div>
          </Slide>
        </BottomActionArea>
      </DesktopOnly>
    </DdPanelView>
  );
};
